import React from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import styled from "styled-components";
import { withTheme } from "styled-components";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import TransparentButton from "components/shared/buttons/TransparentButton";
import { withFormik, FormikValues } from "formik";
import { object, string } from "yup";
import TextField from "@material-ui/core/TextField";
import CallToActionButton from "components/shared/buttons/CallToActionButton";
import { isNil, get } from "lodash";
import ErrorMessage from "components/shared/alerts/ErrorMessage";
import { ApolloConsumer } from "react-apollo";
import gql from "graphql-tag";
import Link from "next/link";
import { useRecordArticleViewed } from "hooks/analytics/useRecordArticleViewed";
import { useRecordReferredArticleEmailCapture } from "hooks/analytics/useRecordReferredArticleEmailCapture";
import { useArticlePageContext } from "contexts/new/ArticlePageContext";

const subscriberHasSharedStoryReadsRemainingQuery = gql`
  query subscriberHasSharedStoryReadsRemainingQuery($email: String!) {
    subscriberHasSharedStoryReadsRemaining(email: $email)
  }
`;

const subscriberExistsQuery = gql`
  query subscriberExistsQuery($email: String!) {
    subscriberExists(email: $email)
  }
`;

const recordSharedArticleReadMutation = gql`
  mutation recordSharedArticleReadMutation($email: String!) {
    signupOrUpdateSubscriberAfterFreeArticleView(email: $email) {
      id
    }
  }
`;

const CloseButton = styled<any>(TransparentButton).attrs({
  className: "w-full mt-2 rounded border border-solid border-gray-200",
})``;

const DialogContainer = styled<any>(Dialog)`
  max-width: 500px;
  margin: 0 auto;
  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 100%;
    max-width: 100%;
  }

  .MuiPaper-root {
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Title = styled<any>(DialogTitle).attrs({ className: "text-center pb-0" })`
  color: ${(props) => props.theme.colors.orange};
`;

const Content = styled<any>(DialogContent).attrs({ className: "p-3" })``;

const Explanation = styled.p.attrs({ className: "text-center py-3" })`
  font-family: ${(props) => props.theme.fonts.poppins};
`;

interface FormProps {
  open: boolean;
  onClose: () => void;
  onLinkRedeemed: () => void;
  storyId: string;
  sharedStoryLinkId: string;
  shareLinkCreatorName: string;
  apolloClient: any;
  recordReferredArticleEmailCapture: (
    email: string,
    subscriberId: string
  ) => void;
}

const querySubscriberExists = async (apolloClient, email) => {
  const result = await apolloClient.query({
    query: subscriberExistsQuery,
    variables: { email },
  });

  return get(result, "data.subscriberExists", false);
};

const queryHasSharedStoryReadsRemaining = async (apolloClient, email) => {
  const result = await apolloClient.query({
    query: subscriberHasSharedStoryReadsRemainingQuery,
    variables: { email },
  });

  return get(result, "data.subscriberHasSharedStoryReadsRemaining", false);
};

const recordSharedArticleRead = async (apolloClient, email) => {
  return await apolloClient.mutate({
    mutation: recordSharedArticleReadMutation,
    variables: { email },
  });
};

const maximumSharedArticlesErrorMessage =
  "Sorry 😢, you've already read the maximum number of shared stories. Subscribe today to keep reading.";

const RedeemShareDialog = withFormik<FormProps, FormikValues>({
  mapPropsToValues: () => ({
    email: "",
    submission: "",
  }),

  validationSchema: object().shape({
    email: string()
      .required("You forgot to enter your email")
      .email("You entered an invalid email address."),
  }),

  handleSubmit: async ({ email }, { props, setErrors, setSubmitting }) => {
    let subscriberIsOutOfSharedStoryReads = false;
    setSubmitting(true);

    try {
      const subscriberExists = await querySubscriberExists(
        props.apolloClient,
        email
      );
      if (subscriberExists) {
        const hasSharedStoryReadsRemaining =
          await queryHasSharedStoryReadsRemaining(props.apolloClient, email);
        if (!hasSharedStoryReadsRemaining) {
          subscriberIsOutOfSharedStoryReads = true;
          throw new Error();
        }
      }

      const response = await recordSharedArticleRead(props.apolloClient, email);
      const subscriberId =
        response?.data?.signupOrUpdateSubscriberAfterFreeArticleView?.id;

      if (subscriberId) {
        props?.recordReferredArticleEmailCapture(email, subscriberId);
      }

      props.onClose();
    } catch (error) {
      console.log(error);
      let errorMessage = "Something went wrong. Please try again";
      setSubmitting(false);
      if (subscriberIsOutOfSharedStoryReads) {
        errorMessage = maximumSharedArticlesErrorMessage;
      }
      setErrors({
        submission: errorMessage,
      });
    }
  },
})(
  //@ts-ignore
  withTheme(
    ({
      shareLinkCreatorEmail,
      story,
      paywalled,
      setErrors,
      setValues,
      submitCount,
      values,
      errors,
      theme,
      open,
      shareLinkCreatorName,
      onClose,
      handleSubmit,
      isSubmitting,
    }) => {
      const isFullScreen = useMediaQuery(
        `(max-width: ${theme.breakpoints.sm})`
      );

      const hasEmailError = !isNil(errors.email) && submitCount > 0;
      const hasSubmissionError = !isNil(errors.submission) && submitCount > 0;

      const isMaximumSharedArticlesError =
        errors.submission === maximumSharedArticlesErrorMessage;

      const hasError =
        (hasEmailError || hasSubmissionError) && !isMaximumSharedArticlesError;

      const hasReadMaximumArticles =
        hasSubmissionError && isMaximumSharedArticlesError;

      useRecordArticleViewed(story, paywalled, shareLinkCreatorEmail);

      return (
        <>
          <ErrorMessage
            show={hasError}
            text={
              hasEmailError
                ? errors.email
                : errors.submission
                ? errors.submission
                : "Something went wrong, please try again."
            }
            onClickClose={() => setErrors({})}
          />
          <DialogContainer
            open={open}
            onClose={() => {}}
            fullScreen={isFullScreen}
          >
            <Title>Read this story</Title>
            <Content>
              {hasReadMaximumArticles ? (
                <>
                  <Explanation>{maximumSharedArticlesErrorMessage}</Explanation>
                  <Link href="/subscribe">
                    <CallToActionButton className="w-full">
                      Subscribe Now
                    </CallToActionButton>
                  </Link>
                </>
              ) : (
                <>
                  <Explanation>
                    {shareLinkCreatorName}, a subscriber of The Juggernaut,
                    shared this article with you. Enter your email to read the
                    full article.
                  </Explanation>
                  <TextField
                    value={values.email}
                    onChange={(e) =>
                      setValues({ ...values, email: e.target.value })
                    }
                    error={hasEmailError}
                    className="w-full mt-2"
                    variant="outlined"
                    label="Email"
                    disabled={isSubmitting}
                  />
                  <CallToActionButton
                    disabled={isSubmitting}
                    className="w-full mt-4"
                    onClick={handleSubmit}
                  >
                    {isSubmitting
                      ? "Checking your email..."
                      : "Read this story"}
                  </CallToActionButton>
                </>
              )}
            </Content>
          </DialogContainer>
        </>
      );
    }
  )
);

export default (props) => {
  const articlePageContext = useArticlePageContext();
  const recordReferredArticleEmailCapture =
    useRecordReferredArticleEmailCapture(articlePageContext);

  return (
    <ApolloConsumer>
      {(apolloClient) => (
        <RedeemShareDialog
          apolloClient={apolloClient}
          {...props}
          recordReferredArticleEmailCapture={recordReferredArticleEmailCapture}
        />
      )}
    </ApolloConsumer>
  );
};
